import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Typography,
    CircularProgress,
    Chip,
    Autocomplete,
    Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { toJalaali } from 'jalaali-js';
import api from '../../../../../config/axiosConfig';
import PersianDatePicker from '../../../../common/PersianDatePicker';
import SearchableAutocomplete from '../../../../SearchableAutocomplete';
import { toPersianDigits, formatNumberInputValue, parseNumberInput } from '../../../../../utils/convertNumbers';
import { API } from '../../../../../utils/apiRoutes';

const PAYMENT_METHOD_CHOICES = [
    { value: 'cash', label: 'نقدی' },
    { value: 'transfer', label: 'انتقال به حساب' },
    { value: 'check', label: 'چک' },
];

const PERSIAN_MONTHS = [
    'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
    'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند',
];

const periodLabel = (value) => {
    if (!value) return '';
    const [y, m] = value.split('-').map(Number);
    return `${PERSIAN_MONTHS[(m || 1) - 1]} ${toPersianDigits(y)}`;
};

// Recent Jalali months (newest first) for the "settlement month" picker.
const buildRecentJalaliMonths = (count = 18) => {
    const now = new Date();
    const { jy, jm } = toJalaali(now.getFullYear(), now.getMonth() + 1, now.getDate());
    const out = [];
    let y = jy, m = jm;
    for (let i = 0; i < count; i++) {
        const value = `${y}-${String(m).padStart(2, '0')}`;
        out.push({ value, label: periodLabel(value) });
        m -= 1;
        if (m === 0) { m = 12; y -= 1; }
    }
    return out;
};
const RECENT_MONTHS = buildRecentJalaliMonths();
const CURRENT_PERIOD = RECENT_MONTHS[0].value;
const TODAY_ISO = new Date().toISOString().split('T')[0];

function TeacherSettlementForm({ settlement, defaults, onSuccess, onCancel }) {
    const theme = useTheme();
    const [formData, setFormData] = useState({
        teacher: null,
        teacher_id: null,
        amount: '',
        payment_date: TODAY_ISO,
        settlement_period: CURRENT_PERIOD,
        payment_method: '',
        reference_number: '',
        description: '',
        receipt: null,
        related_operations_ids: [],
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [receiptPreview, setReceiptPreview] = useState(null);
    const [availableOperations, setAvailableOperations] = useState([]);
    const [loadingOperations, setLoadingOperations] = useState(false);
    const [payable, setPayable] = useState(null); // payable/مانده for the chosen person + month
    const [personType, setPersonType] = useState('teacher'); // 'teacher' | 'staff'
    const isStaff = personType === 'staff';
    const personLabel = isStaff ? 'پرسنل' : 'آموزگار';

    // Look up how much is owed for the selected person + settlement month, to
    // show as a helper above the amount field.
    useEffect(() => {
        const tid = formData.teacher_id;
        const period = formData.settlement_period;
        if (!tid || !period) { setPayable(null); return; }
        let cancelled = false;
        const params = { person: personType, year_month: period, [isStaff ? 'staff_id' : 'teacher_id']: tid };
        api.get(API.teacherSettlementTransactions, { params })
            .then((res) => { if (!cancelled) setPayable(res.data); })
            .catch(() => { if (!cancelled) setPayable(null); });
        return () => { cancelled = true; };
    }, [formData.teacher_id, formData.settlement_period, personType, isStaff]);

    useEffect(() => {
        if (settlement) {
            // Edit mode - populate form with existing data
            const editIsStaff = settlement.staff !== null && settlement.staff !== undefined;
            setPersonType(editIsStaff ? 'staff' : 'teacher');
            const personId = editIsStaff ? settlement.staff : settlement.teacher;
            const personName = editIsStaff ? settlement.staff_name : settlement.teacher_name;
            const teacherOption = personId
                ? {
                    id: personId,
                    user: {
                        id: personId,
                        first_name: personName?.split(' ')[0] || '',
                        last_name: personName?.split(' ').slice(1).join(' ') || '',
                    }
                }
                : null;

            const relatedOpsIds = Array.isArray(settlement.related_operations)
                ? settlement.related_operations
                : (settlement.related_operations || []);

            setFormData({
                teacher: teacherOption,
                teacher_id: personId || null,
                amount: settlement.amount || '',
                payment_date: settlement.payment_date || '',
                settlement_period: settlement.settlement_period || CURRENT_PERIOD,
                payment_method: settlement.payment_method || '',
                reference_number: settlement.reference_number || '',
                description: settlement.description || '',
                receipt: null, // Don't pre-populate file input
                related_operations_ids: relatedOpsIds,
            });
            if (settlement.receipt) {
                setReceiptPreview(settlement.receipt);
            }

            // Load related operations only for teachers (staff have no sessions).
            if (personId && !editIsStaff) {
                loadAvailableOperations(personId);
            }
        } else {
            // Create mode - reset form, applying any prefilled defaults (e.g. when
            // adding a payment from a person's ledger: person + month known).
            const createIsStaff = defaults?.person_type === 'staff';
            setPersonType(createIsStaff ? 'staff' : 'teacher');
            const teacherOption = defaults?.teacher_id
                ? {
                    id: defaults.teacher_id,
                    user: {
                        id: defaults.teacher_id,
                        first_name: defaults.teacher_name?.split(' ')[0] || '',
                        last_name: defaults.teacher_name?.split(' ').slice(1).join(' ') || '',
                    },
                }
                : null;
            setFormData({
                teacher: teacherOption,
                teacher_id: defaults?.teacher_id || null,
                amount: '',
                payment_date: TODAY_ISO,
                settlement_period: defaults?.settlement_period || CURRENT_PERIOD,
                payment_method: '',
                reference_number: '',
                description: '',
                receipt: null,
                related_operations_ids: [],
            });
            setReceiptPreview(null);
            setAvailableOperations([]);
            if (defaults?.teacher_id && !createIsStaff) {
                loadAvailableOperations(defaults.teacher_id);
            }
        }
    }, [settlement, defaults]);

    const loadAvailableOperations = async (teacherId) => {
        if (!teacherId) {
            setAvailableOperations([]);
            return;
        }

        setLoadingOperations(true);
        try {
            const response = await api.get(API.operationBrowser, {
                params: {
                    teacher_id: teacherId,
                    page_size: 100, // Get more operations
                }
            });
            const operations = response.data?.results || response.data || [];
            setAvailableOperations(operations);
        } catch (error) {
            console.error('Error loading operations:', error);
            setAvailableOperations([]);
        } finally {
            setLoadingOperations(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleTeacherChange = (teacherId, teacherOption) => {
        handleChange('teacher_id', teacherId);
        handleChange('teacher', teacherOption);
        
        // Load available operations for this teacher
        loadAvailableOperations(teacherId);
        
        // Clear related operations when teacher changes
        if (formData.teacher_id !== teacherId) {
            handleChange('related_operations_ids', []);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0] || null;
        handleChange('receipt', file);
        if (file) {
            // Show file name as preview
            setReceiptPreview(file.name);
        } else {
            setReceiptPreview(null);
        }
    };

    const validate = () => {
        const newErrors = {};
        
        if (!formData.teacher_id) {
            newErrors.teacher_id = `لطفاً ${personLabel} را انتخاب کنید`;
        }
        
        if (!formData.amount || formData.amount <= 0) {
            newErrors.amount = 'لطفاً مبلغ معتبری وارد کنید';
        }
        
        if (!formData.payment_date) {
            newErrors.payment_date = 'لطفاً تاریخ پرداخت را انتخاب کنید';
        }
        
        if (!formData.payment_method) {
            newErrors.payment_method = 'لطفاً روش پرداخت را انتخاب کنید';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validate()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append(isStaff ? 'staff' : 'teacher', formData.teacher_id);
            formDataToSend.append('amount', formData.amount);
            formDataToSend.append('payment_date', formData.payment_date);
            if (formData.settlement_period) {
                formDataToSend.append('settlement_period', formData.settlement_period);
            }
            formDataToSend.append('payment_method', formData.payment_method);
            if (formData.reference_number) {
                formDataToSend.append('reference_number', formData.reference_number);
            }
            if (formData.description) {
                formDataToSend.append('description', formData.description);
            }
            if (formData.receipt) {
                formDataToSend.append('receipt', formData.receipt);
            }
            // Append related operations as array
            if (formData.related_operations_ids && formData.related_operations_ids.length > 0) {
                formData.related_operations_ids.forEach((opId) => {
                    formDataToSend.append('related_operations_ids', opId);
                });
            }

            if (settlement) {
                // Update existing settlement
                await api.patch(`${API.teacherSettlements}${settlement.id}/`, formDataToSend, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            } else {
                // Create new settlement
                await api.post(API.teacherSettlements, formDataToSend, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            }

            onSuccess();
        } catch (error) {
            console.error('Error saving settlement:', error);
            if (error.response?.data) {
                // Set field-specific errors if available
                const fieldErrors = {};
                Object.keys(error.response.data).forEach(key => {
                    if (Array.isArray(error.response.data[key])) {
                        fieldErrors[key] = error.response.data[key][0];
                    } else {
                        fieldErrors[key] = error.response.data[key];
                    }
                });
                setErrors(fieldErrors);
            } else {
                setErrors({ submit: 'خطا در ذخیره تسویه. لطفاً دوباره تلاش کنید.' });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '';
        return toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount));
    };

    const getOperationLabel = (operation) => {
        if (!operation) return '';
        const studentName = operation.student?.full_name || '-';
        const courseName = operation.course?.course_name || '-';
        const date = operation.date_belonged 
            ? new Date(operation.date_belonged).toLocaleDateString('fa-IR')
            : '-';
        const amount = operation.teacher_wage 
            ? formatCurrency(operation.teacher_wage)
            : '-';
        return `${studentName} - ${courseName} - ${date} - ${amount} تومان`;
    };

    // Filter operations to show only those with teacher_wage > 0
    const payableOperations = availableOperations.filter(op => (op.teacher_wage || 0) > 0);

    // Settlement-month options; include the current value if it's older than the list.
    const periodOptions = (formData.settlement_period && !RECENT_MONTHS.some(o => o.value === formData.settlement_period))
        ? [{ value: formData.settlement_period, label: periodLabel(formData.settlement_period) }, ...RECENT_MONTHS]
        : RECENT_MONTHS;

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
                {payable && (
                    <Grid item xs={12}>
                        <Alert severity={(payable.remaining_balance || 0) > 0 ? 'info' : 'success'}>
                            قابل پرداخت {periodLabel(formData.settlement_period)}:{' '}
                            <Box component="span" sx={{ fontWeight: 'bold' }}>
                                {toPersianDigits(new Intl.NumberFormat('fa-IR').format(payable.remaining_balance || 0))} تومان
                            </Box>
                        </Alert>
                    </Grid>
                )}

                <Grid item xs={12} sm={6}>
                    <SearchableAutocomplete
                        key={personType}
                        apiUrl={isStaff ? '/accounts/api/v1/profile/staffs/' : '/accounts/api/v1/profile/teachers/'}
                        getOptionLabel={(option) =>
                            `${option.user?.first_name || ''} ${option.user?.last_name || ''}`.trim() || option.user?.email || ''
                        }
                        getOptionValue={(option) => option?.user?.id}
                        value={formData.teacher_id || null}
                        onChange={handleTeacherChange}
                        label={personLabel}
                        required
                        error={errors.teacher_id}
                        initialOption={formData.teacher}
                    />
                    {errors.teacher_id && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                            {errors.teacher_id}
                        </Typography>
                    )}
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="مبلغ (تومان)"
                        type="text"
                        inputMode="numeric"
                        value={formatNumberInputValue(formData.amount)}
                        onChange={(e) => {
                            const parsed = parseNumberInput(e.target.value, false); // false = integer only
                            handleChange('amount', parsed);
                        }}
                        error={!!errors.amount}
                        helperText={errors.amount}
                        required
                        inputProps={{ min: 0 }}
                    />
                    {formData.amount && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {formatCurrency(formData.amount)} تومان
                        </Typography>
                    )}
                </Grid>

                <Grid item xs={12} sm={6}>
                    <PersianDatePicker
                        label="تاریخ پرداخت"
                        value={formData.payment_date ? new Date(formData.payment_date) : null}
                        onChange={(date) => {
                            if (date && !isNaN(date.getTime())) {
                                handleChange('payment_date', date.toISOString().split('T')[0]);
                            } else {
                                handleChange('payment_date', '');
                            }
                        }}
                        error={!!errors.payment_date}
                        helperText={errors.payment_date}
                        required
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.settlement_period}>
                        <InputLabel>ماه تسویه</InputLabel>
                        <Select
                            value={formData.settlement_period}
                            label="ماه تسویه"
                            onChange={(e) => handleChange('settlement_period', e.target.value)}
                        >
                            {periodOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                            ماهی که این پرداخت بابت آن است (مستقل از تاریخ پرداخت)
                        </Typography>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.payment_method} required>
                        <InputLabel>روش پرداخت</InputLabel>
                        <Select
                            value={formData.payment_method}
                            label="روش پرداخت"
                            onChange={(e) => handleChange('payment_method', e.target.value)}
                        >
                            {PAYMENT_METHOD_CHOICES.map((choice) => (
                                <MenuItem key={choice.value} value={choice.value}>
                                    {choice.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.payment_method && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                {errors.payment_method}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="شماره مرجع"
                        value={formData.reference_number}
                        onChange={(e) => handleChange('reference_number', e.target.value)}
                        error={!!errors.reference_number}
                        helperText={errors.reference_number}
                        placeholder="مثال: شماره چک یا شماره تراکنش"
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="توضیحات"
                        multiline
                        rows={4}
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        error={!!errors.description}
                        helperText={errors.description}
                    />
                </Grid>

                {!isStaff && (
                <Grid item xs={12}>
                    <Autocomplete
                        multiple
                        options={payableOperations}
                        getOptionLabel={getOperationLabel}
                        value={payableOperations.filter(op =>
                            formData.related_operations_ids.includes(op.id)
                        )}
                        onChange={(event, newValue) => {
                            handleChange('related_operations_ids', newValue.map(op => op.id));
                        }}
                        loading={loadingOperations}
                        disabled={!formData.teacher_id || loadingOperations}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="عملیات‌های مرتبط (اختیاری)"
                                placeholder={formData.teacher_id
                                    ? "عملیات قابل پرداخت این آموزگار را انتخاب کنید"
                                    : "ابتدا آموزگار را انتخاب کنید"
                                }
                                helperText="می‌توانید عملیات‌هایی که این پرداخت برای آن‌هاست را انتخاب کنید"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={getOperationLabel(option)}
                                    {...getTagProps({ index })}
                                    size="small"
                                    key={option.id}
                                />
                            ))
                        }
                    />
                </Grid>
                )}

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        type="file"
                        label="رسید پرداخت"
                        onChange={handleFileChange}
                        error={!!errors.receipt}
                        helperText={errors.receipt || (receiptPreview && `فایل انتخاب شده: ${receiptPreview}`)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ accept: 'image/*,.pdf' }}
                    />
                    {settlement?.receipt && !formData.receipt && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            فایل فعلی: <a href={settlement.receipt} target="_blank" rel="noopener noreferrer">مشاهده</a>
                        </Typography>
                    )}
                </Grid>

                {errors.submit && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="error">
                            {errors.submit}
                        </Typography>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            onClick={onCancel}
                            disabled={isSubmitting}
                            variant="outlined"
                        >
                            انصراف
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                        >
                            {isSubmitting ? 'در حال ذخیره...' : (settlement ? 'ویرایش' : 'ثبت')}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default TeacherSettlementForm;

